import { LiveData, Service } from '@toeverything/infra';

import { RealtimeLiveQuery } from '../realtime/live-query';
import type {
  UpdateUserSettingsInput,
  UserSettings,
  UserSettingsStore,
} from '../stores/user-settings';

export type { UserSettings };

type PendingAccountColor = {
  color: string | null;
  resolve: () => void;
  reject: (error: unknown) => void;
};

export class UserSettingsService extends Service {
  constructor(private readonly store: UserSettingsStore) {
    super();
    this.liveQuery.start();
  }

  userSettings$ = new LiveData<UserSettings | undefined>(undefined);
  isLoading$ = new LiveData<boolean>(false);
  error$ = new LiveData<any | undefined>(undefined);
  private readonly pendingAccountColors$ = new LiveData(
    new Map<string, PendingAccountColor>()
  );
  private colorSaveTimer?: ReturnType<typeof setTimeout>;
  private savingAccountColors = false;
  private disposed = false;

  readonly calendarAccountColors$ = LiveData.computed(get => {
    const colors = new Map(
      (get(this.userSettings$)?.calendarAccountColors ?? []).map(entry => [
        entry.accountId,
        entry.color,
      ])
    );
    for (const [accountId, pending] of get(this.pendingAccountColors$)) {
      if (pending.color === null) colors.delete(accountId);
      else colors.set(accountId, pending.color);
    }
    return Array.from(colors, ([accountId, color]) => ({ accountId, color }));
  });

  setCalendarAccountColor(
    accountId: string,
    color: string | null
  ): Promise<void> {
    if (this.disposed || !this.userSettings$.value)
      return Promise.reject(new Error('User settings are not loaded'));
    if (color !== null && !/^#[0-9a-f]{6}$/i.test(color))
      return Promise.reject(new Error('Invalid calendar color'));
    return new Promise((resolve, reject) => {
      const pending = new Map(this.pendingAccountColors$.value);
      pending.get(accountId)?.resolve();
      pending.set(accountId, { color, resolve, reject });
      this.pendingAccountColors$.value = pending;
      clearTimeout(this.colorSaveTimer);
      this.colorSaveTimer = setTimeout(() => {
        this.colorSaveTimer = undefined;
        this.saveAccountColors().catch(error => {
          this.error$.value = error;
        });
      }, 300);
    });
  }

  private async saveAccountColors() {
    if (
      this.disposed ||
      this.savingAccountColors ||
      !this.pendingAccountColors$.value.size
    )
      return;
    this.savingAccountColors = true;
    const batch = new Map(this.pendingAccountColors$.value);
    const colors = this.calendarAccountColors$.value;
    let error: unknown;
    let failed = false;
    try {
      await this.updateUserSettings({ calendarAccountColors: colors });
    } catch (cause) {
      failed = true;
      error = cause;
    } finally {
      const pending = new Map(this.pendingAccountColors$.value);
      for (const [accountId, entry] of batch) {
        if (pending.get(accountId) === entry) {
          pending.delete(accountId);
          if (failed) entry.reject(error);
          else entry.resolve();
        }
      }
      this.pendingAccountColors$.value = pending;
      this.savingAccountColors = false;
      if (!this.colorSaveTimer)
        this.saveAccountColors().catch(error => {
          this.error$.value = error;
        });
    }
  }
  private readonly liveQuery = new RealtimeLiveQuery({
    request: signal => this.requestUserSettings(signal),
    subscribe: () => this.store.subscribeUserSettings(),
    applySnapshot: settings => {
      this.error$.value = undefined;
      this.userSettings$.value = settings;
    },
    applyEvent: () => 'revalidate' as const,
    onError: error => {
      this.error$.value = error;
    },
  });

  revalidate = () => {
    this.liveQuery.revalidate();
  };

  async updateUserSettings(settings: UpdateUserSettingsInput) {
    await this.store.updateUserSettings(settings);
    this.userSettings$.value = {
      ...this.userSettings$.value,
      ...(settings as UserSettings),
    };
    this.revalidate();
  }

  override dispose(): void {
    this.disposed = true;
    clearTimeout(this.colorSaveTimer);
    for (const entry of this.pendingAccountColors$.value.values())
      entry.resolve();
    this.pendingAccountColors$.value = new Map();
    super.dispose();
    this.liveQuery.dispose();
  }

  private async requestUserSettings(signal: AbortSignal) {
    this.isLoading$.value = true;
    try {
      return await this.store.getUserSettings(signal);
    } finally {
      this.isLoading$.value = false;
    }
  }
}
